import { View, Text, Image, Link, StyleSheet } from "@react-pdf/renderer";
import { getSportLabel } from "@/lib/sport-defaults";
import {
  verdictLabel,
  type CityReportStation,
} from "@/components/city-review/types";
import { palette, verdictStatus } from "./styles";

export interface StationBlockComment {
  id: string;
  comment: string;
  created_at: string;
}

interface StationBlockProps {
  station: CityReportStation;
  photoDataUrls: Record<string, string>;
  comments: StationBlockComment[];
}

const styles = StyleSheet.create({
  block: {
    flexDirection: "column",
    marginBottom: palette.spacing.lg,
    padding: palette.spacing.md,
    borderWidth: 0.5,
    borderStyle: "solid",
    borderColor: palette.border,
    borderRadius: 6,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: palette.spacing.md,
  },
  headerLeft: {
    flexDirection: "column",
    flex: 1,
    paddingRight: palette.spacing.md,
  },
  stationLabel: {
    fontSize: palette.type.h3,
    fontFamily: "Helvetica-Bold",
    color: palette.text,
  },
  stationAddress: {
    marginTop: 2,
    fontSize: palette.type.small,
    color: palette.muted,
  },
  mapsLink: {
    marginTop: 4,
    fontSize: palette.type.small,
    color: palette.brand,
    textDecoration: "underline",
  },
  pill: {
    alignSelf: "flex-start",
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 999,
    fontSize: palette.type.tiny,
    fontFamily: "Helvetica-Bold",
  },
  bodyRow: {
    flexDirection: "row",
  },
  leftCol: {
    width: "55%",
    paddingRight: palette.spacing.md,
    flexDirection: "column",
  },
  rightCol: {
    width: "45%",
    flexDirection: "column",
  },
  eyebrow: {
    fontSize: palette.type.tiny,
    color: palette.muted,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: palette.spacing.xs,
  },
  lockerRow: {
    flexDirection: "row",
    paddingVertical: 1,
  },
  lockerLabel: {
    fontSize: palette.type.small,
    color: palette.text,
    width: 70,
  },
  lockerSport: {
    fontSize: palette.type.small,
    color: palette.muted,
  },
  photoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -2,
    marginVertical: -2,
  },
  photoCell: {
    width: "50%",
    padding: 2,
  },
  photoFrame: {
    width: "100%",
    aspectRatio: 1,
    borderRadius: 4,
    overflow: "hidden",
    backgroundColor: palette.softBg,
  },
  photoImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  noPhotos: {
    fontSize: palette.type.small,
    color: palette.muted,
    fontStyle: "italic",
  },
  commentsBlock: {
    marginTop: palette.spacing.md,
    paddingTop: palette.spacing.md,
    borderTopWidth: 0.5,
    borderTopStyle: "solid",
    borderTopColor: palette.border,
    flexDirection: "column",
  },
  commentsHeader: {
    fontSize: palette.type.h3,
    fontFamily: "Helvetica-Bold",
    color: palette.text,
    marginBottom: palette.spacing.sm,
  },
  commentItem: {
    flexDirection: "column",
    paddingVertical: palette.spacing.sm,
    borderTopWidth: 0.5,
    borderTopStyle: "solid",
    borderTopColor: palette.border,
  },
  commentItemFirst: {
    borderTopWidth: 0,
  },
  commentMeta: {
    fontSize: palette.type.tiny,
    color: palette.muted,
    fontStyle: "italic",
    marginBottom: 2,
  },
  commentBody: {
    fontSize: palette.type.small,
    color: palette.text,
    lineHeight: 1.4,
  },
});

function formatCommentDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function StationBlock({
  station,
  photoDataUrls,
  comments,
}: StationBlockProps) {
  const status = verdictStatus(station.city_verdict);
  const pillColors = palette.status[status];
  const mapsUrl = `https://www.google.com/maps?q=${station.lat},${station.lng}`;

  // Filter photos down to those we actually have data URLs for so we don't
  // emit broken <Image> nodes that would surface as a render error.
  const renderablePhotos = station.photos.filter(
    (photo) => photoDataUrls[photo.id],
  );

  return (
    <View style={styles.block} wrap={false}>
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <Text style={styles.stationLabel}>{station.label}</Text>
          {station.address ? (
            <Text style={styles.stationAddress}>{station.address}</Text>
          ) : null}
          <Link src={mapsUrl} style={styles.mapsLink}>
            View on Google Maps
          </Link>
        </View>
        <Text
          style={[
            styles.pill,
            {
              backgroundColor: pillColors.soft,
              color: pillColors.solid,
            },
          ]}
        >
          {verdictLabel(station.city_verdict)}
        </Text>
      </View>

      <View style={styles.bodyRow}>
        <View style={styles.leftCol}>
          {station.lockers.length > 0 ? (
            <View>
              <Text style={styles.eyebrow}>Lockers</Text>
              {station.lockers.map((locker) => (
                <View key={locker.locker_index} style={styles.lockerRow}>
                  <Text style={styles.lockerLabel}>
                    Locker #{locker.locker_index}
                  </Text>
                  <Text style={styles.lockerSport}>
                    {locker.sport_type
                      ? getSportLabel(locker.sport_type)
                      : "Unassigned"}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}
        </View>

        <View style={styles.rightCol}>
          <Text style={styles.eyebrow}>Photos</Text>
          {renderablePhotos.length === 0 ? (
            <Text style={styles.noPhotos}>No photos</Text>
          ) : (
            <View style={styles.photoGrid}>
              {renderablePhotos.map((photo) => {
                const src = photoDataUrls[photo.id];
                if (!src) return null;
                return (
                  <View key={photo.id} style={styles.photoCell}>
                    <View style={styles.photoFrame}>
                      <Image src={src} style={styles.photoImage} />
                    </View>
                  </View>
                );
              })}
            </View>
          )}
        </View>
      </View>

      {comments.length > 0 ? (
        <View style={styles.commentsBlock}>
          <Text style={styles.commentsHeader}>Comments</Text>
          {comments.map((comment, idx) => (
            <View
              key={comment.id}
              style={[
                styles.commentItem,
                idx === 0 ? styles.commentItemFirst : {},
              ]}
            >
              <Text style={styles.commentMeta}>
                {formatCommentDate(comment.created_at)}
              </Text>
              <Text style={styles.commentBody}>{comment.comment}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}
